import { $success, $black, $white } from 'constants/styles';
import {
	opsViewUsersCircleLayerId,
	opsViewAircraftLayerId,
	opsViewUsersCluserCircleLayerId
} from 'components/opsView/opsViewMapConstants';

export const defaultMapCenter = [-95.9325, 41.256_03]; //Center of USA
export const defaultZoomedOutZoom = 4;
export const defaultZoomedInZoom = 12;

export const defaultDrawingFillColor = 'rgba(54, 209, 138, .8)';
export const defaultDrawingLineColor = $success;

export const rectangleDrawMode = 'drag_rectangle';
export const circleDrawMode = 'drag_circle';
export const polygonDrawMode = 'draw_polygon';
export const simpleSelectMode = 'simple_select';
export const directSelectMode = 'direct_select';
export const staticMode = 'static';
export const measurementDrawMode = 'measurement_draw';
export const measurementSelectMode = 'measurement_select';
export const measurementDirectSelectMode = 'measurement_direct_select';
export const lineDrawMode = 'draw_line_string';
export const pointDrawMode = 'draw_point';

export const createMapDrawStyles = (
	fillColor = defaultDrawingFillColor,
	lineColor = defaultDrawingLineColor,
	pointColor = defaultDrawingLineColor,
	pointIcon = 'pin',
	polygonLineColor = defaultDrawingLineColor
) => {
	return [
		// LineString (in-progress or final) - regular lines only
		{
			id: 'gl-draw-line',
			type: 'line',
			filter: ['all', ['==', '$type', 'LineString'], ['!=', 'user_isMeasurement', true]],
			layout: {
				'line-cap': 'round',
				'line-join': 'round'
			},
			paint: {
				// If currently drawing a polygon, use polygonLineColor for the in-progress edge; otherwise use lineColor
				'line-color': [
					'case',
					['==', ['get', 'mode'], polygonDrawMode],
					polygonLineColor,
					lineColor
				],
				'line-width': 6
			}
		},
		// Measurement LineString (separate style with dashed line)
		{
			id: 'gl-draw-line-measurement',
			type: 'line',
			filter: ['all', ['==', '$type', 'LineString'], ['==', 'user_isMeasurement', true]],
			layout: {
				'line-cap': 'round',
				'line-join': 'round'
			},
			paint: {
				'line-color': '#000000',
				'line-width': 4,
				'line-dasharray': [2, 2]
			}
		},
		// Measurement Label (text label showing distance)
		{
			id: 'gl-draw-line-measurement-label',
			type: 'symbol',
			filter: ['all', ['==', '$type', 'LineString'], ['==', 'user_isMeasurement', true]],
			layout: {
				'symbol-placement': 'line-center',
				'text-size': 28,
				'text-anchor': 'center',
				'text-justify': 'center',
				'text-allow-overlap': false,
				'text-ignore-placement': false,
				'text-field': ['concat', ['get', 'distance'], ' ', ['get', 'unit']],
				'text-font': ['Open Sans Bold'],
				'icon-allow-overlap': false,
				'icon-ignore-placement': false,
				'text-offset': [0, -2]
			},
			minzoom: 0,
			paint: {
				'text-color': '#000000',
				'text-halo-color': '#ffffff',
				'text-halo-width': 1
			}
		},
		{
			id: 'gl-static-line-measurement-label',
			type: 'symbol',
			filter: ['all', ['==', 'isMeasurementLabel', true]],
			layout: {
				'symbol-placement': 'point',
				'text-size': [
					'interpolate',
					['linear'],
					['zoom'],
					5,
					0,
					8,
					16, // At zoom level 8, text size 16
					12,
					20, // At zoom level 12, text size 20
					16,
					24 // At zoom level 16, text size 24
				],
				'text-anchor': 'center',
				'text-justify': 'center',
				'text-allow-overlap': false,
				'text-ignore-placement': false,
				'text-field': ['get', 'label'],
				'text-font': ['Open Sans Bold'],
				'icon-allow-overlap': true,
				'icon-ignore-placement': true
			},
			minzoom: 3,
			paint: {
				'text-color': $black,
				'text-halo-color': $white,
				'text-halo-width': 3,
				'text-halo-blur': 1
			}
		},

		// Polygon fill
		{
			id: 'gl-draw-polygon-fill',
			type: 'fill',
			filter: ['all', ['==', '$type', 'Polygon']],
			paint: {
				'fill-color': fillColor
			}
		},

		// Polygon stroke
		{
			id: 'gl-draw-polygon-stroke-active',
			type: 'line',
			filter: ['all', ['==', '$type', 'Polygon']],
			paint: {
				'line-color': polygonLineColor,
				'line-width': 6
			},
			layout: {
				'line-join': 'round',
				'line-cap': 'round'
			}
		},

		// Vertex halo (for drag handles)
		{
			id: 'gl-draw-polygon-and-line-vertex-halo-active',
			type: 'symbol',
			filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
			paint: {
				'icon-opacity': 1
			},
			layout: {
				'icon-image': 'hospital-15',
				'icon-size': 1
			}
		},

		// Vertex points (circle)
		{
			id: 'gl-draw-vertex-points',
			type: 'circle',
			filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
			paint: {
				'circle-radius': 10,
				'circle-color': lineColor,
				'circle-opacity': 0.75
			}
		},

		// Midpoints (smaller, lighter)
		{
			id: 'circle-midpoint',
			type: 'circle',
			filter: ['all', ['==', 'meta', 'midpoint'], ['==', '$type', 'Point']],
			paint: {
				'circle-radius': 5,
				'circle-color': $black,
				'circle-opacity': 0.25
			}
		},
		{
			id: 'gl-draw-point-icon',
			type: 'symbol',
			filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
			layout: {
				'icon-image': pointIcon,
				'icon-size': 1,
				'icon-allow-overlap': true
			},
			paint: {
				'icon-color': pointColor
			}
		}
	];
};

export const lineLayout = {
	'line-join': 'round',
	'line-cap': 'round'
};

export const linePaint = {
	'line-opacity': 1,
	'line-width': 6
};

export const fillPaint = {
	'fill-opacity': 1
};

// export const defaultMapStyle = 'kittyhawkio/ckrzr4dac081917mwbg7up7sf';
export const defaultMapStyle = 'ac-default';

export const defaultMapboxStyleValue = 'mapbox://styles/mapbox/streets-v11';

export const mapboxMapStyles = [
	{
		value: 'mapbox://styles/mapbox/streets-v11',
		name: 'Streets'
	},
	{
		value: 'mapbox://styles/mapbox/outdoors-v11',
		name: 'Outdoors'
	},
	{
		value: 'mapbox://styles/mapbox/light-v10',
		name: 'Light'
	},
	{
		value: 'mapbox://styles/mapbox/dark-v10',
		name: 'Dark'
	},
	{
		value: 'mapbox://styles/mapbox/satellite-v9',
		name: 'Satellite'
	},
	{
		value: 'mapbox://styles/mapbox/satellite-streets-v11',
		name: 'Satellite Streets'
	},
	{
		value: 'mapbox://styles/mapbox/navigation-day-v1',
		name: 'Navigation Day'
	},
	{
		value: 'mapbox://styles/mapbox/navigation-night-v1',
		name: 'Navigation Night'
	}
];

export const liveTrafficLayerId = 'live_traffic_layer';
export const liveTrafficOutlineLayerId = 'live_traffic_outline_layer';
export const liveTrafficDoubleOutlineLayerId = 'live_traffic_double_outline_layer';
export const liveTrafficTrailsLayerId = 'live_traffic_trails_layer';
export const liveTrafficSourceId = 'live_traffic_source';
export const selectedFeatureSourceId = 'selected_feature_source';
export const selectedFeatureLayerId = 'selected_feature_layer';
export const liveTrafficTrailsSourceId = 'live_traffic_trails_source';

export const showAircraftInfoSourceId = 'show_aircraft_info_source';
export const showAircraftInfoLayerId = 'show_aircraft_info_layer';

export const operatingPictureSourceId = 'operating_picture_source';
export const operatingPictureLineLayerId = 'operating_picture_line_layer';
export const operatingPictureFillLayerId = 'operating_picture_fill_layer';
export const operatingPictureSymbolLayerId = 'operating_picture_symbol_layer';

export const ownshipSourceId = 'ownship-source';
export const ownshipFeaturesLineLayerId = 'ownship_features_line_layer';
export const ownshipTextLayerId = 'ownship_text_layer';
export const ownshipRingLabelsLayerId = 'ownship_ring_labels_layer';

export const streamsSourceId = 'streams_source';
export const streamsLayerId = 'streams_layer';

export const alwaysClickableLayers = [
	opsViewUsersCluserCircleLayerId,
	opsViewAircraftLayerId,
	opsViewUsersCircleLayerId,
	liveTrafficLayerId,
	showAircraftInfoLayerId, // Make the aircraft info text layer clickable
	operatingPictureFillLayerId,
	operatingPictureSymbolLayerId,
	streamsLayerId
];

export const noTranslateLayers = ['road-number-shield'];

export const greenLinePaintStyle = {
	'line-opacity': 1,
	'line-width': 6,
	'line-color': $success
};

export const greenFillPaintStyle = {
	'fill-opacity': 0.5,
	'fill-color': $success
};

// Bearing/Range Line Constants
export const bearingRangeLinesSourceId = 'bearing-range-lines-source';
export const bearingRangeLinesLayerId = 'bearing-range-lines';
export const bearingRangeTextLayerId = 'bearing-range-text';

export const blueLowAltitudeOwnshipProviders = [
	'dronetag_private',
	'airmesh_sdk',
	'aloft_simulator'
];

export const userLocationSourceId = 'user_location';

export const uasfmAirspaceSourceId = 'mapbox_faa_uasfms';

export const getDrawModeName = drawMode => {
	switch (drawMode) {
		case polygonDrawMode:
			return 'polygon';
		case circleDrawMode:
			return 'circle';
		case rectangleDrawMode:
			return 'rectangle';
		case lineDrawMode:
			return 'line';
		case pointDrawMode:
			return 'point';
		case measurementDrawMode:
			return 'measurement';
		default:
			return '';
	}
};

export const selectedAircraftsViewAndCursorSourceId = 'selected-aircrafts-view-and-cursor-source';
export const selectedAircraftsViewAndCursorLayerId = 'selected-aircrafts-view-and-cursor-layer';

export const mapHoverSelectBufferInPixels = 10;
